import PropTypes from 'prop-types';
import React, {useEffect, useState} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {bindActionCreators} from 'redux';

import {validateBackupRestoreInProgress} from 'stores/business/backupAndRestoreBusiness';
import {selectIsInstrumentOperational} from 'stores/selectors/instrumentSelectors';
import {selectIsInstrumentControlHubConnected} from 'stores/selectors/signalRSelectors';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {LoginBox} from '../../components';
import {FastRobotErrorcode, Feedback, UpdateState, ViewMode} from '../../constants/dataEnum';
import withIntl from '../../lib/withIntl';
import {changeView, hideLoading, hideSnackbar, showLoading, showSnackbar} from '../../stores/actions/appStateAction';
import {
  getIsAutomationMode,
  getFastInfo,
  getIsSimulationMode,
  getInstrumentStatus,
  setInstrumentBasedOnRuntimeState,
} from '../../stores/business/instrumentBusiness';
import {
  login,
  proceedNewState,
  restoreState,
  setAutosaveProtocolState,
  takeOverSession,
} from '../../stores/business/sessionBusiness';
import {fetchExplorerViewSettings} from '../../stores/business/userManagementBusiness';
import {
  getCurrentVersionData,
  getNewSoftwareUpdate,
  getSoftwareUpdateStatus,
  prepareSoftwareUpdate,
  showUpdateCompleteLoadingScreen,
} from '../../stores/business/softwareUpdaterBusiness';
import {showFailedToUpdateSystemModal} from '../../stores/business/modalStateBusinessAction';
import {fastCommonActuatorAPIErrorHandler} from '../../stores/business/errorHandlerBusiness';

const mapStateToProps = (state) => ({
  configStateVersion: state.configState.version,
  appStateViewMode: state.appState.viewMode,
  isTessaraInstrument: state.licenseState.isTessaraInstrument,
});

const mapDispatchToProps = (dispatch) => ({
  appStateActionChangeView: bindActionCreators(changeView, dispatch),
  sessionBusinessLogin: bindActionCreators(login, dispatch),
  sessionBusinessRestoreState: bindActionCreators(restoreState, dispatch),
  sessionBusinessTakeOverSession: bindActionCreators(takeOverSession, dispatch),
  softwareUpdaterBusinessGetSoftwareUpdateStatus: bindActionCreators(getSoftwareUpdateStatus, dispatch),
  softwareUpdaterBusinessShowUpdateCompleteLoading: bindActionCreators(showUpdateCompleteLoadingScreen, dispatch),
  softwareUpdaterBusinessPrepareSoftwareUpdate: bindActionCreators(prepareSoftwareUpdate, dispatch),
  softwareUpdaterBusinessGetNewSoftwareUpdate: bindActionCreators(getNewSoftwareUpdate, dispatch),
  instrumentHubBusinessGetFastInfo: bindActionCreators(getFastInfo, dispatch),
  modalStateBusinessActionShowFailedToUpdateSystemModal: bindActionCreators(showFailedToUpdateSystemModal, dispatch),
  appStateActionShowLoading: bindActionCreators(showLoading, dispatch),
  appStateActionHideLoading: bindActionCreators(hideLoading, dispatch),
  appStateActionHideSnackbar: bindActionCreators(hideSnackbar, dispatch),
  errorHandlerBusinessfastCommonActuatorAPIErrorHandler: bindActionCreators(
    fastCommonActuatorAPIErrorHandler,
    dispatch,
  ),
});

function LoginPage(props) {
  const {
    configStateVersion,
    appStateViewMode,
    isTessaraInstrument,
    appStateActionChangeView,
    sessionBusinessLogin,
    sessionBusinessRestoreState,
    sessionBusinessTakeOverSession,
    softwareUpdaterBusinessGetSoftwareUpdateStatus,
    softwareUpdaterBusinessShowUpdateCompleteLoading,
    softwareUpdaterBusinessPrepareSoftwareUpdate,
    // softwareUpdaterBusinessGetNewSoftwareUpdate,
    instrumentHubBusinessGetFastInfo,
    modalStateBusinessActionShowFailedToUpdateSystemModal,
    appStateActionShowLoading,
    appStateActionHideLoading,
    appStateActionHideSnackbar,
    intl,
    errorHandlerBusinessfastCommonActuatorAPIErrorHandler,
  } = props;

  const dispatch = useDispatch();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loginResult, setLoginResult] = useState({});
  const [currentVersionYear, setCurrentVersionYear] = useState('');
  const isInstrumentOperational = useSelector(selectIsInstrumentOperational);
  const isInstrumentControlHubConnected = useSelector(selectIsInstrumentControlHubConnected);

  const onUsernameChangeHandler = (evt) => {
    setUsername(evt.target.value);
  };

  const onPasswordChangeHandler = (evt) => {
    setPassword(evt.target.value);
  };

  const retryUpdate = async () => {
    appStateActionShowLoading(
      <FormattedHTMLMessage id="about.updaterLoading.updating.text" values={{percentage: 0}} />,
    );

    const response = await softwareUpdaterBusinessPrepareSoftwareUpdate();
    if (response === FastRobotErrorcode.NoError) {
      softwareUpdaterBusinessShowUpdateCompleteLoading();
    } else {
      appStateActionHideLoading();
      modalStateBusinessActionShowFailedToUpdateSystemModal(retryUpdate);
    }
  };

  // TODO: move to sessionBusiness
  const proceedRestoreState = async (response) => {
    const {user, session, visionCompatibility} = response;

    await sessionBusinessTakeOverSession(user);
    await sessionBusinessRestoreState(session?.content);
    await dispatch(setAutosaveProtocolState());

    if (visionCompatibility !== FastRobotErrorcode.NoError) {
      errorHandlerBusinessfastCommonActuatorAPIErrorHandler(visionCompatibility);
    }
  };

  // TO-DO DH: Refactor this function to be more straight forward.
  const onLoginHandler = async () => {
    appStateActionHideSnackbar();
    appStateActionShowLoading(intl.formatMessage({id: 'login.LoginBoxContainer.loginLoading'}));

    let isUpdating;
    let userId;
    let progress;
    let errorCode;
    let state;

    if (isInstrumentOperational) {
      const isBackupRestoreInProgress = await dispatch(validateBackupRestoreInProgress());

      if (isBackupRestoreInProgress) {
        dispatch(hideLoading());
        dispatch(showSnackbar(Feedback.INFO, intl.formatMessage({id: 'snackbar.restoreData.inProgress'})));

        return;
      }

      const updateStatus = await softwareUpdaterBusinessGetSoftwareUpdateStatus();

      if (updateStatus) {
        isUpdating = updateStatus.isUpdating;
        userId = updateStatus.userId;
        progress = updateStatus.progress;
        errorCode = updateStatus.errorCode;
        state = updateStatus.state;
      }

      if (isUpdating) {
        await instrumentHubBusinessGetFastInfo();
      }

      const instrumentStatus = await dispatch(getInstrumentStatus());

      dispatch(setInstrumentBasedOnRuntimeState(instrumentStatus));
    }

    const result = await sessionBusinessLogin(username, password);

    setUsername('');
    setPassword('');

    if (!result) {
      return;
    }

    setLoginResult({...result});

    if (isUpdating && errorCode !== FastRobotErrorcode.NoError) {
      await appStateActionChangeView(ViewMode.ABOUT);
      modalStateBusinessActionShowFailedToUpdateSystemModal(retryUpdate);

      return;
    }

    if (isUpdating && userId === result.user.userId && (state === UpdateState.PREPARING || progress < 100)) {
      await appStateActionChangeView(ViewMode.ABOUT);
      appStateActionShowLoading(
        <FormattedHTMLMessage id="about.updaterLoading.updating.text" values={{percentage: progress}} />,
      );

      return;
    }

    if (isUpdating && userId === result.user.userId && (state === UpdateState.PREPARED || progress >= 100)) {
      await appStateActionChangeView(ViewMode.ABOUT);
      softwareUpdaterBusinessShowUpdateCompleteLoading();

      return;
    }

    const {loginConfirmation} = result;

    if (loginConfirmation.show) {
      await appStateActionChangeView(ViewMode.LOGIN_CONFIRMATION);
    } else if (!loginConfirmation.show) {
      if (loginConfirmation.restoreState) {
        await proceedRestoreState(result);
      } else {
        await dispatch(proceedNewState(result));
      }
    }

    if (result?.user?.userId) {
      await dispatch(fetchExplorerViewSettings(result?.user?.userId));
    }
  };

  const handleRestoreClick = () => {
    proceedRestoreState(loginResult);
  };

  const handleNewStateClick = () => {
    dispatch(proceedNewState(loginResult));
  };

  const onKeyPressEnterHandler = () => {
    onLoginHandler();
  };

  const isRestoringState = () => appStateViewMode !== ViewMode.LOGIN;

  const getCurrentVersionYear = async () => {
    const currentVerData = await getCurrentVersionData();
    const {releaseDate} = currentVerData?.data || {};
    const year = new Date(releaseDate).getFullYear();

    setCurrentVersionYear(year);
  };

  useEffect(() => {
    const getInstrumentStatus = async () => {
      dispatch(getIsSimulationMode());
      dispatch(getIsAutomationMode());
    };

    if (isInstrumentControlHubConnected && isInstrumentOperational) {
      getInstrumentStatus();
    }
    getCurrentVersionYear();
  }, [dispatch, isInstrumentControlHubConnected, isInstrumentOperational]);

  return (
    <LoginBox
      logoIconSrc="./svg/login/fast-icon.svg"
      logoIconAlt="FAST Icon"
      logoTextSrc="./svg/login/fast-text.svg"
      logoTextAlt="Fast Liquid Handler"
      logoFooterSrc="./svg/login/formulatrix.svg"
      logoFooterAlt="Formulatrix Logo"
      logoTessaraSrc="./svg/login/tessara.svg"
      logoTessaraAlt="Tessara Logo"
      version={intl.formatMessage({id: 'login.LoginBoxContainer.loginFooter'}, {version: configStateVersion})}
      year={currentVersionYear}
      isRestoringState={isRestoringState()}
      isRestoringMessage={
        // eslint-disable-next-line react/jsx-wrap-multilines
        <>
          <p>{intl.formatMessage({id: 'login.LoginBoxContainer.headerTextRestore.div1'})}</p>
          <p>{intl.formatMessage({id: 'login.LoginBoxContainer.headerTextRestore.div2'})}</p>
        </>
      }
      isRestoringRestoreButtonText={intl.formatMessage({
        id: 'login.LoginBoxContainer.buttonApplyRestore',
      })}
      isRestoringNewStateButtonText={intl.formatMessage({
        id: 'login.LoginBoxContainer.buttonCancelRestore',
      })}
      username={username}
      password={password}
      loginButtonText={intl.formatMessage({id: 'login.LoginBoxContainer.buttonSignIn'})}
      isTessaraInstrument={isTessaraInstrument}
      onUsernameChange={onUsernameChangeHandler}
      onUsernameKeyPressEnter={onKeyPressEnterHandler}
      onPasswordChange={onPasswordChangeHandler}
      onPasswordKeyPressEnter={onKeyPressEnterHandler}
      onLoginClick={onLoginHandler}
      onRestoreClick={handleRestoreClick}
      onNewStateClick={handleNewStateClick}
    />
  );
}

LoginPage.propTypes = {
  configStateVersion: PropTypes.any.isRequired,
  appStateViewMode: PropTypes.any.isRequired,
  isTessaraInstrument: PropTypes.bool.isRequired,
  appStateActionChangeView: PropTypes.func.isRequired,
  sessionBusinessLogin: PropTypes.func.isRequired,
  sessionBusinessRestoreState: PropTypes.func.isRequired,
  sessionBusinessTakeOverSession: PropTypes.func.isRequired,
  softwareUpdaterBusinessGetSoftwareUpdateStatus: PropTypes.func.isRequired,
  softwareUpdaterBusinessPrepareSoftwareUpdate: PropTypes.func.isRequired,
  softwareUpdaterBusinessShowUpdateCompleteLoading: PropTypes.func.isRequired,
  instrumentHubBusinessGetFastInfo: PropTypes.func.isRequired,
  modalStateBusinessActionShowFailedToUpdateSystemModal: PropTypes.func.isRequired,
  appStateActionShowLoading: PropTypes.func.isRequired,
  appStateActionHideLoading: PropTypes.func.isRequired,
  appStateActionHideSnackbar: PropTypes.func.isRequired,
  intl: PropTypes.object.isRequired,
  errorHandlerBusinessfastCommonActuatorAPIErrorHandler: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(withIntl(LoginPage));
